<template>
  <transition name="fade" mode="out-in">
    <div v-if="!isPendingSpace" class="wrapper" :key="space?.workspace_id">
      <Header
        v-bind="space"
      />
      <div class="data-card card">
        <div class="button-header">
          <Button :disabled="!canWriteCampaignAndSpaces(roles)" class="icon icon-left outline-white light-text" :label="$t('app.spaces.spaceView.settings')" @click="state.modalIsOpen = true">
            <template #prefix>
              <GearIcon style="width: 16px;"></GearIcon>
            </template>
          </Button>
          <Button :disabled="!canWriteCampaignAndSpaces(roles)" class="icon icon-left primary light-text" :label="$t('app.spaces.spaceView.createCampaign')" @click="goToCreateCampaign">
            <template #prefix>
              <PlusIcon style="width: 16px;"></PlusIcon>
            </template>
          </Button>
          <VField class="light put-to-end search" :placeholder="$t('app.campaign.single.search')" name="search" icon="bi-search" v-model="state.search.value" @input="(event) => (state.search.value = event.target.value)" />
        </div>
        <Table
          v-if="!isPending"
          :data="filteredCampaigns"
          :fields="state.fields"
          :itemsPerPage="100"
          @clickOnRow="
            (campaign: Campaign) =>
              $router.push({
                name: 'campaign',
                params: {
                  campaignId: campaign.id,
                },
              })
          "
        >
          <template #status="{ data }">
            <div class="tag" :class="data.status ? 'green' : 'grey-light'" v-text="$t(data.status ? 'spaces.spaceView.finished' : 'spaces.spaceView.inProgress')" />
          </template>
          <template #created_at="{ data }">
            <div
              :style="{ width: 'fit-content' }"
              v-text="getDate(data.created_at, 'LL')"
              v-tooltip="{
                content: getDate(data.created_at, 'LLLL'),
                placement: 'top',
              }"
            />
          </template>
          <template #actions="{ row }">
            <Button :disabled="!canWriteCampaignAndSpaces(roles)" :label="$t('app.spaces.spaceView.campaignSettings')" class="secondary outline icon" icon="bi-gear" @click="openParameters(row)" />
            <div @click.stop="null">
              <VDropdown :disabled="!canWriteCampaignAndSpaces(roles)" :distance="10" placement="left">
                <div class="btn outline secondary icon square">
                  <i class="bi bi-three-dots-vertical" />
                </div>
                <template #popper>
                  <div class="action" v-close-popper @click="archive(row)">
                    <i class="bi bi-arrow-counterclockwise" />
                    <span>{{ $t("app.spaces.spaceView.archiveCampaign") }}</span>
                  </div>
                </template>
              </VDropdown>
            </div>
          </template>
          <template #empty>
            <Empty
              :message="$t('app.spaces.spaceView.noCampaign')"
              :action="{
                label: $t('app.spaces.spaceView.createCampaign'),
                disabled: !canWriteCampaignAndSpaces(roles),
                routeParams: {
                  name: 'newCampaign',
                  query: {
                    spaceId: route.params.spaceId,
                  },
                },
              }"
              :dark="isDark"
              icon="box"
            />
          </template>
        </Table>
      </div>
      <SpaceModal v-if="!isPendingSpace" :isOpen="state.modalIsOpen" :space="space" action="edit" @close="state.modalIsOpen = false" />
      <ActionModal modal="archive" :data="state.selectedCampaign" :isOpen="state.showActionModal" @close="state.showActionModal = false" @confirm="state.showActionModal = false" />
    </div>
  </transition>
</template>

<style lang="scss" scoped>
  .button-header {
    display: flex;
    flex-direction: row;
    gap: 8px;

    .put-to-end {
      margin-left: auto;
    }
  }

  .search {
    width: 350px;
  }
</style>

<script setup lang="ts">
import { reactive } from "vue";
import { useRoute } from "vue-router";
import { useDark } from "@vueuse/core";

import { getDate, dateIsBefore } from "@/composables/utils.js";
import { canWriteCampaignAndSpaces } from "@/composables/permissions";

import Header from "@/components/Header.vue";
import Empty from "@/components/table/Empty.vue";
import SpaceModal from "@/components/modals/app/SpaceModal.vue";
import ActionModal from "@/components/modals/app/actionModal/ActionModal.vue";

import { useCampaigns } from "@/queries/campaigns.query";
import { useRoles } from "@/queries/user.query";
import { useSpace } from "@/queries/space.query";

import { Campaign } from "@/types/campaign";
import PlusIcon from '@/assets/images/icon/campaign/plus.svg?component';
import GearIcon from '@/assets/images/icon/campaign/gear.svg?component';

import codeRomeData from '@/assets/correspondance_rome/correspondance_code_rome.json';
import intituleRomeData from '@/assets/correspondance_rome/correspondance_intitule_rome.json';

interface JobCategorie {
  categorie_number: number,
  generic_categorie: string,
}

type RomeMap = Record<string, JobCategorie>;

const codeRome = Object.entries(codeRomeData as RomeMap);
const intituleRome = Object.entries(intituleRomeData as RomeMap);

const isDark = useDark();
const route = useRoute();
const router = useRouter();
const appStore = useAppStore();
const { t } = useI18n();

const { data: roles, isPending: isRolesPending } = useRoles();
const { data: space, isPending: isPendingSpace } = useSpace();
const { data: campaigns, isPending } = useCampaigns();

const state = reactive({
  selectedCampaign: {} as Campaign,
  modalIsOpen: false,
  showParameters: false,
  showActionModal: false,
  campaign: null,
  search: {
    value: "",
    searchable: [
      "campaign.internal_name",
    ],
  },
  fields: [
    {
      id: "campaign.internal_name",
      label: t("app.spaces.spaceView.campaignName"),
      defaultSortDir: "DESC",
      multipleSort: true,
      sort(a, b, dir) {
        console.log(a, b, dir);
        if (dir === "ASC") return a.campaign.internal_name.localeCompare(b.campaign.internal_name);
        return b.campaign.internal_name.localeCompare(a.campaign.internal_name);
      },
    },
    {
      id: "created_at",
      label: t("app.spaces.spaceView.creationDate"),
      defaultSortDir: "DESC",
      sort(a, b, dir) {
        if (dir === "ASC") {
          return dateIsBefore(b, a) ? 1 : -1;
        } else {
          return dateIsBefore(a, b) ? 1 : -1;
        }
      },
    },
  ],
});

const filteredCampaigns = computed(() => {
  if (isPending.value) return [];
  const searchString = state.search.value.trim().toLocaleLowerCase();
  if (searchString === "") return [...campaigns.value];

  const filteredRomeIntitule = intituleRome.filter((intitule) => {
    return intitule[0].includes(searchString);
  });

  const filteredRomeCode = codeRome.filter((code) => {
    return code[0].toLocaleLowerCase() === searchString;
  });

  const res = campaigns.value.filter((campaign) => {
    const campaignName = campaign.campaign.internal_name.trim().toLocaleLowerCase();
    if (campaignName.includes(searchString)) return true;
    if (filteredRomeIntitule.find((intitule) => campaignName.includes(intitule[1].generic_categorie))) return true;
    if (filteredRomeCode.find((code) => campaignName.includes(code[1].generic_categorie))) return true;
    return false;
  });
  return res;
});

function goToCreateCampaign() {
  router.push({
    name: 'newCampaign',
    query: {
      spaceId: route.params.spaceId,
    },
  })
}

async function archive(campaign: Campaign) {
  state.selectedCampaign = campaign;
  state.showActionModal = true;
}

function openParameters(row: Campaign) {
  state.selectedCampaign = row;
  appStore.currentCampaignTab = 'Parameters';
  appStore.campaignParametersTab = 0;
  router.push({
    name: 'campaign',
    params: {
      campaignId: state.selectedCampaign.id || '8d9740ed-56ac-4f1f-856f-df9d81f16c52',
    },
  });
}
</script>
